import React from 'react';

import { prop } from 'ramda';
import { Field, Form, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import Button from 'components/shared/Button';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import FormErrorText from 'components/shared/FormErrorText';
import Loader from 'components/shared/Loader';
import { Select } from 'components/shared/Select/adapters/FinalForm';
import { DeleteAction } from 'components/shared/Select/components/DeleteAction';
import { OptionWithAction } from 'components/shared/Select/options/OptionWithAction';
import FormTitle from 'components/shared/Typography/FormTitle';
import { ACCOUNT_TYPE_GUEST } from 'constants/index';
import { useRelocationPoints } from 'hooks/useRelocationPointMutations/relocationPoints';
import useValidationSchema from 'hooks/useValidationSchema';
import { accountTypeSelector } from 'store/user/selectors';

import { FlightsContext } from '../../context';
import s from '../index.module.css';
import { useForms } from '../useForms';
import { validationSchema } from './validationSchema';

const RelocationPointField = function (props) {
  const { values } = useFormState();

  const { deleteRelocationPoint, createRelocationPoint, relocationPoints } = useRelocationPoints([
    values.departureFrom,
    values.arrivalTo,
  ]);

  const OptionWithDeleteMemoized = React.useCallback((props) => {
    const onClickDelete = (e) => {
      e.stopPropagation();
      deleteRelocationPoint(props.data.value);
    };

    return <OptionWithAction {...props} action={<DeleteAction onClick={onClickDelete} />} />;
  }, []);

  const onCreateRelocationPoint = (inputValue) => {
    createRelocationPoint(inputValue);
    return { label: inputValue, value: inputValue };
  };

  return (
    <Field
      component={FormGroupAdapter}
      customComponent={Select}
      options={relocationPoints}
      optionToString
      creatable
      editable
      components={{ Option: OptionWithDeleteMemoized }}
      onCreateOption={onCreateRelocationPoint}
      classNames={{ control: () => s.control, valueContainer: () => s.valueContainer }}
      {...props}
    />
  );
};

const TransferResolveField = function (props) {
  const isGuest = useSelector(accountTypeSelector) === ACCOUNT_TYPE_GUEST;

  if (isGuest) return <Field component={FormGroupAdapter} {...props} />;
  return <RelocationPointField {...props} />;
};

const ManuallyTransferForm = function ({ handleSubmit, submitting, values, submitError, hasValidationErrors }) {
  const btnText = values?.id ? 'Edit Transfer' : 'Add Transfer';

  const { dateFormat } = React.useContext(FlightsContext);

  return (
    <form autoComplete="nope">
      {submitting && <Loader isFillBlock />}

      <FormTitle>I. Departure</FormTitle>

      <Field component={FormGroupAdapter} name="group1" type="inputGroup">
        <Field
          component={FormGroupAdapter}
          name="departureDate"
          placeholder="09/09/2021"
          label="Date*"
          type="date"
          dateFormat={dateFormat}
        />
        <Field component={FormGroupAdapter} name="departureTime" placeholder="09:00" label="Time" type="time" />
        <TransferResolveField name="departureFrom" placeholder="Arusha" label="Departing From*" />
      </Field>

      <FormTitle>II. Arrival </FormTitle>

      <Field component={FormGroupAdapter} name="group2" type="inputGroup">
        <Field
          // key required for rerender picker on default month change
          key={'arrivalDateField-' + values.departureDate}
          component={FormGroupAdapter}
          name="arrivalDate"
          placeholder="09/09/2021"
          label="Date*"
          type="date"
          highlighted={values.departureDate}
          defaultMonth={values.departureDate}
          dateFormat={dateFormat}
        />
        <Field component={FormGroupAdapter} name="arrivalTime" placeholder="09:00" label="Time" type="time" />
        <TransferResolveField name="arrivalTo" placeholder="Manyara" label="Arrival To*" />
      </Field>

      <FormTitle>III. Transfer Details</FormTitle>

      <Field component={FormGroupAdapter} name="group3" type="inputGroup">
        <Field
          component={FormGroupAdapter}
          name="bookedBy"
          placeholder="Nomad Tanzania"
          label="Booked By"
          parse={(value) => (value === '' ? null : value)}
        />
        <Field
          component={FormGroupAdapter}
          name="confirmationNumber"
          placeholder="AGH7BE"
          label="Confirmation Number"
          parse={(value) => (value === '' ? null : value)}
        />
      </Field>

      <Field component={FormGroupAdapter} name="group4" type="inputGroup">
        <Field
          component={FormGroupAdapter}
          name="provider"
          placeholder="Nomad Tanzania"
          label="Provider"
          parse={(value) => (value === '' ? null : value)}
        />
        <Field
          component={FormGroupAdapter}
          name="contactInformation"
          placeholder="+255 756 6746 7593"
          label="Contact Information"
          parse={(value) => (value === '' ? null : value)}
        />
      </Field>

      <Field component={FormGroupAdapter} name="group5" type="inputGroup">
        <Field
          component={FormGroupAdapter}
          name="duration"
          placeholder="2 hours"
          label="Duration"
          parse={(value) => (value === '' ? null : value)}
        />
        <div />
      </Field>

      {submitError && <FormErrorText>{submitError}</FormErrorText>}
      {/* <pre style={{ fontSize: '16px' }}>{JSON.stringify(values, 0, 2)}</pre> */}

      <FormActions isRight>
        <Button
          htmlType="button"
          type={hasValidationErrors ? 'default' : 'primary'}
          disabled={hasValidationErrors || submitting}
          onClick={handleSubmit}
        >
          {btnText}
        </Button>
      </FormActions>
    </form>
  );
};

export function ManuallyTransfer(props) {
  const { currentItem } = props;
  const { handleSubmitTransfer, getManuallyInitialValues } = useForms(props);
  const validate = useValidationSchema(validationSchema);
  const initialValues = getManuallyInitialValues(currentItem);

  const { withPointsResolve } = useRelocationPoints([initialValues.departureFrom, initialValues.arrivalTo]);

  const handleSubmit = withPointsResolve(handleSubmitTransfer, [prop('departureFrom'), prop('arrivalTo')]);

  return (
    <Form onSubmit={handleSubmit} initialValues={initialValues} validate={validate} render={ManuallyTransferForm} />
  );
}
